import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
  Slider,
  Space,
  Spin,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  CompressOutlined,
  DeleteOutlined,
  ExpandOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  ReloadOutlined,
  SaveOutlined,
  ZoomInOutlined,
} from "@ant-design/icons";
import type { UploadProps } from "antd";
import {
  configOptionApi,
  ImageOptimizationSettings,
} from "../../services/configOption.api";
import {
  compressImage,
  dataUrlBytes,
  imageDimensions,
  IMAGE_OPT_DEFAULTS,
  invalidateImageOptimizationConfig,
  supportsWebP,
} from "../../utils/imageCompression";
import { getMsg } from "../../utils/antdStatic";
import { useClipboardPaste } from "../../Components/DragDropUpload";
import InfoAyuda, { AyudaBloque } from "../../Components/InfoAyuda";

const { Text, Title } = Typography;

/**
 * Explicación COMPLETA del flujo de fotos (el (i) junto al título). Cada afirmación sale de
 * docs/config-optimizacion-imagenes.md, de `utils/imageCompression.ts` y de
 * `storage.service.js` (EXIF, miniatura) / `limiteImagen.js` (50 MB): si el comportamiento
 * cambia, hay que cambiar esto también.
 */
const AyudaImagenes = () => (
  <InfoAyuda titulo="Cómo funcionan las fotos" texto="¿Cómo funciona?" testId="ayuda-imagenes">
    <AyudaBloque titulo="Qué es">
      Las fotos se comprimen en el <b>navegador</b> de quien las sube, antes de enviarlas: así se
      gastan menos datos al subir y el servidor guarda menos peso. Esta pestaña fija con qué
      parámetros se comprime. Solo el administrador la cambia; los clientes, las aseadoras y los
      demás usuarios únicamente ven las fotos que les corresponden.
    </AyudaBloque>

    <AyudaBloque titulo="El recorrido de una foto">
      <ol style={{ margin: 0, paddingLeft: 18 }}>
        <li>La persona elige, arrastra, pega o toma la foto.</li>
        <li>
          Si es una foto <b>HEIC del iPhone</b> y el navegador no la entiende (Chrome, Firefox,
          Edge), se convierte a JPEG ahí mismo con una librería que se descarga solo cuando hace
          falta. Safari la lee solo. Si no se puede convertir, se avisa y la foto no se sube.
        </li>
        <li>
          Se reduce al <i>tamaño máximo</i>, se codifica en el formato elegido y, si pesa más que
          el <i>peso objetivo</i>, se baja primero la calidad y después el tamaño, sin pasar del{" "}
          <i>tamaño mínimo</i>. Una foto que ya es chica y liviana se deja como está.
        </li>
        <li>
          Se sube al servidor, que rechaza las fotos de más de <b>50 MB</b>. Algunas pantallas
          avisan antes con un tope propio de 25 MB sobre el archivo original.
        </li>
        <li>
          Al guardarla, el servidor le quita los datos de ubicación y de la cámara (GPS, EXIF),
          la endereza según su orientación y, en JPEG y WebP, la vuelve a codificar con calidad
          alta (92). Por eso el peso final guardado puede diferir algo del que ves en la prueba
          de esta pantalla, que no incluye este último paso.
        </li>
        <li>
          Se entrega con enlaces firmados que no cambian durante una hora: el navegador reutiliza
          las fotos que ya descargó (hasta 6 horas) en vez de bajarlas otra vez.
        </li>
      </ol>
    </AyudaBloque>

    <AyudaBloque titulo="Miniaturas">
      De cada foto el servidor genera automáticamente una <b>miniatura de 400 px</b> (WebP). Las
      listas y las grillas usan la miniatura para gastar muchos menos datos, y la foto completa
      se descarga solo cuando se abre (una foto antigua sin miniatura se muestra completa). El
      tamaño de la miniatura no se configura aquí.
    </AyudaBloque>

    <AyudaBloque titulo="Dónde se aplica esta compresión">
      Tareas, Inventario, Finanzas empresa y las Notas del aseo, además del editor de fotos
      (Tareas, Inventario y la foto de perfil). Las demás pantallas con fotos (Flujos de caja,
      información para el huésped de las propiedades, Agenda de contactos y Costos sugeridos)
      suben la foto tal como se elige, sin esta compresión; el servidor igual le quita la
      ubicación y crea la miniatura.
    </AyudaBloque>

    <AyudaBloque titulo="Cómo se gestiona el espacio">
      El módulo <b>Almacenamiento</b> (solo administradores) muestra cuánto pesa cada archivo,
      permite eliminar con confirmación y muestra el consumo de datos. Los videos se configuran
      en la pestaña «Optimización de videos».
    </AyudaBloque>

    <AyudaBloque titulo="Qué NO hace">
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>
          No cambia las fotos que ya estaban subidas: se comprimen una sola vez, al subirlas. Para
          mejorar una foto antigua hay que subirla de nuevo.
        </li>
        <li>
          Los cambios los toman otros dispositivos con sesión abierta en hasta 5 minutos (o al
          refrescar la página).
        </li>
        <li>
          La prueba de esta pantalla no guarda nada: solo muestra cómo quedaría una foto con los
          valores del formulario.
        </li>
      </ul>
    </AyudaBloque>
  </InfoAyuda>
);

/** Tooltip text de cada campo — en español neutro, panhispánico. */
const HELP = {
  maxSide:
    "Tamaño máximo de la imagen (lado más largo, en píxeles). Si la foto es más grande, se redimensiona. Más alto = mejor calidad pero archivo más pesado. Recomendado: 2000-2800 px.",
  minSide:
    "Tamaño mínimo permitido. Si al comprimir hay que bajar más de este valor para alcanzar el peso objetivo, se detiene y conserva este mínimo. Evita que la foto quede inservible.",
  quality:
    "Calidad JPEG/WebP. 1.0 = máxima calidad (archivo pesado), 0.5 = muy comprimida. Recomendado: 0.82-0.92.",
  targetKB:
    "Peso objetivo en KB. El optimizador intenta no pasar este peso; si no lo logra manteniendo la calidad, prioriza calidad y queda un poco por arriba.",
  format:
    "Formato de salida. 'auto' usa WebP cuando el navegador lo soporta (mucho mejor compresión a misma calidad visual). 'jpeg' es el más compatible; 'webp' fuerza WebP siempre.",
};

const FORMAT_OPTIONS = [
  { value: "auto", label: "auto (WebP si el navegador lo soporta)" },
  { value: "jpeg", label: "JPEG (máxima compatibilidad)" },
  { value: "webp", label: "WebP (mejor compresión)" },
];

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes < 0) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

interface PreviewStats {
  dataUrl: string;
  bytes: number;
  width: number;
  height: number;
}

/** Tamaño del círculo-lupa en píxeles. */
const LENS_SIZE = 160;
const ZOOM_MIN = 1.5;
const ZOOM_MAX = 10;

/** Posición de la lupa en coordenadas relativas a la imagen (0–1). */
interface LensPos {
  x: number;
  y: number;
}

interface MagnifierImageProps {
  src: string;
  alt: string;
  lensPos: LensPos | null;
  onLensChange: (pos: LensPos | null) => void;
  zoom: number;
  onZoomDelta: (delta: number) => void;
  /** Si es true, la imagen se muestra a resolución natural con scroll
   *  (lo que ves es lo que queda en CF al tamaño real de la galería).
   *  Si es false, se ajusta al ancho del contenedor (vista miniatura). */
  fullSize: boolean;
}

/**
 * Imagen con lupa circular opcional controlada desde afuera. La lupa
 * se pinta si `lensPos` es no-null; eso permite que DOS componentes
 * MagnifierImage compartan la misma posición relativa y el mouse sobre
 * cualquiera de los dos mueva la lupa en ambos simultáneamente.
 * La rueda del mouse sobre la imagen ajusta el zoom compartido.
 */
function MagnifierImage({
  src,
  alt,
  lensPos,
  onLensChange,
  zoom,
  onZoomDelta,
  fullSize,
}: MagnifierImageProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Medimos el tamaño renderizado del <img> para poder ubicar la lupa en
  // coordenadas absolutas dentro del wrapper.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const update = () => setDims({ w: img.offsetWidth, h: img.offsetHeight });
    if (img.complete) update();
    img.addEventListener("load", update);
    const ro = new ResizeObserver(update);
    ro.observe(img);
    return () => {
      img.removeEventListener("load", update);
      ro.disconnect();
    };
  }, [src]);

  // `onWheel` en React es passive por defecto → no podemos prevenir el
  // scroll de la página con `e.preventDefault()`. Registramos listener
  // nativo con `{ passive: false }`.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      onZoomDelta(e.deltaY < 0 ? 0.5 : -0.5);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onZoomDelta]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    onLensChange({ x, y });
  };

  const handleMouseLeave = () => onLensChange(null);

  const lensStyle: React.CSSProperties | null =
    lensPos && dims.w > 0 && dims.h > 0
      ? {
          position: "absolute",
          left: lensPos.x * dims.w - LENS_SIZE / 2,
          top: lensPos.y * dims.h - LENS_SIZE / 2,
          width: LENS_SIZE,
          height: LENS_SIZE,
          borderRadius: "50%",
          pointerEvents: "none",
          border: "2px solid #fff",
          boxShadow:
            "0 0 0 1px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.45)",
          backgroundImage: `url(${src})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: `${dims.w * zoom}px ${dims.h * zoom}px`,
          backgroundPosition: `${
            -(lensPos.x * dims.w * zoom - LENS_SIZE / 2)
          }px ${-(lensPos.y * dims.h * zoom - LENS_SIZE / 2)}px`,
          zIndex: 2,
        }
      : null;

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        display: "inline-block",
        cursor: "crosshair",
        userSelect: "none",
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        style={
          fullSize
            ? { display: "block" } // tamaño natural; el contenedor externo scrollea
            : { maxWidth: "100%", maxHeight: 320, display: "block" }
        }
      />
      {lensStyle && <div style={lensStyle} />}
    </div>
  );
}

export default function ImageOptimizationTab() {
  const [form] = Form.useForm<ImageOptimizationSettings>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSettings, setSavedSettings] = useState<ImageOptimizationSettings | null>(
    null,
  );
  const [defaults, setDefaults] = useState<ImageOptimizationSettings>(
    IMAGE_OPT_DEFAULTS as ImageOptimizationSettings,
  );

  // Preview side-by-side
  const [originalImage, setOriginalImage] = useState<PreviewStats | null>(null);
  const [optimizedImage, setOptimizedImage] = useState<PreviewStats | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  // Token de corrida: descarta resultados obsoletos si el usuario cambia un
  // campo mientras la optimización previa aún está corriendo.
  const runTokenRef = useRef(0);

  // Estado compartido de la lupa: la misma posición relativa (0–1) se
  // pinta en ambas imágenes, movida por el mouse en cualquiera de las
  // dos. El zoom se controla con la rueda y con el slider.
  const [lensPos, setLensPos] = useState<LensPos | null>(null);
  const [zoom, setZoom] = useState(3);
  // Tamaño real vs ajustado. En "Ajustado" la imagen se encoge a 320px
  // de alto — engañosa porque oculta la pérdida de calidad de una foto
  // comprimida (a 320px todo se ve bien). En "Real" la imagen se
  // muestra a resolución natural con scroll, lo que coincide con cómo
  // la galería de Tareas y el lightbox la abren (60vh y 100% real).
  const [fullSize, setFullSize] = useState(false);
  const clampZoom = useCallback(
    (z: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z)),
    [],
  );
  const handleZoomDelta = useCallback(
    (delta: number) => setZoom((z) => clampZoom(z + delta)),
    [clampZoom],
  );

  const handleClearImage = () => {
    setOriginalImage(null);
    setOptimizedImage(null);
    setLensPos(null);
  };

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const res = await configOptionApi.getImageOptimization();
    if (res) {
      setSavedSettings(res.data);
      setDefaults(res.defaults);
      form.setFieldsValue(res.data);
    } else {
      // Backend aún no disponible: usamos los defaults del frontend.
      form.setFieldsValue(IMAGE_OPT_DEFAULTS as ImageOptimizationSettings);
    }
    setLoading(false);
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const res = await configOptionApi.updateImageOptimization(values);
    if (res) {
      setSavedSettings(res.data);
      // Invalidar cache para que los callers tomen la nueva config YA.
      invalidateImageOptimizationConfig();
    }
    setSaving(false);
  };

  const handleRestoreDefaults = () => {
    form.setFieldsValue(defaults);
  };

  // Regenera el preview optimizado usando los valores actuales del form.
  const regenerateOptimized = useCallback(
    async (original: PreviewStats | null) => {
      if (!original) {
        setOptimizedImage(null);
        return;
      }
      const token = ++runTokenRef.current;
      setOptimizing(true);
      try {
        const values = form.getFieldsValue() as ImageOptimizationSettings;
        const compressed = await compressImage(original.dataUrl, values);
        if (token !== runTokenRef.current) return; // resultado obsoleto
        if (!compressed) {
          setOptimizedImage(null);
          return;
        }
        const dims = await imageDimensions(compressed);
        if (token !== runTokenRef.current) return;
        setOptimizedImage({
          dataUrl: compressed,
          bytes: dataUrlBytes(compressed),
          width: dims.width,
          height: dims.height,
        });
      } catch (e: any) {
        if (token !== runTokenRef.current) return;
        getMsg()?.error(e?.message || "Error al optimizar la imagen");
        setOptimizedImage(null);
      } finally {
        if (token === runTokenRef.current) setOptimizing(false);
      }
    },
    [form],
  );

  // Lógica de subida compartida (drag, click y paste). El UploadProps wrapper
  // se mantiene por compat con `<Upload>` de AntD; el handler real es
  // `processIncomingFile`.
  const processIncomingFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      getMsg()?.error("Selecciona un archivo de imagen");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      try {
        const dims = await imageDimensions(dataUrl);
        const stats: PreviewStats = {
          dataUrl,
          bytes: dataUrlBytes(dataUrl),
          width: dims.width,
          height: dims.height,
        };
        setOriginalImage(stats);
        regenerateOptimized(stats);
      } catch {
        getMsg()?.error("No se pudo cargar la imagen");
      }
    };
    reader.readAsDataURL(file);
  }, [regenerateOptimized]);

  const handleFileUpload: UploadProps["beforeUpload"] = (file) => {
    processIncomingFile(file);
    return Upload.LIST_IGNORE;
  };

  // Convención del proyecto (storage-y-visor.md §6): toda zona de upload
  // soporta paste. Acá el listener vive en el tab — admin puede pegar una
  // captura cualquier momento mientras está probando configuraciones.
  useClipboardPaste({
    active: true,
    onFile: processIncomingFile,
    accept: "image/*",
  });

  // Cuando cambia cualquier valor del form, regenerar el preview si ya
  // hay imagen cargada. Usamos debounce leve (250 ms) para evitar
  // disparos excesivos mientras el usuario escribe en el InputNumber.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleFormChange = useCallback(() => {
    if (!originalImage) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      regenerateOptimized(originalImage);
    }, 250);
  }, [originalImage, regenerateOptimized]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const reductionPct = useMemo(() => {
    if (!originalImage || !optimizedImage) return null;
    if (originalImage.bytes === 0) return null;
    return (
      ((originalImage.bytes - optimizedImage.bytes) / originalImage.bytes) * 100
    );
  }, [originalImage, optimizedImage]);

  const renderLabel = (text: string, tooltip: string) => (
    <span>
      {text}{" "}
      <Tooltip title={tooltip}>
        <InfoCircleOutlined style={{ color: "#aaa" }} />
      </Tooltip>
    </span>
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Space align="center" wrap style={{ marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          Optimización de imágenes
        </Title>
        <AyudaImagenes />
      </Space>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="¿Cuándo se aplican estos parámetros?"
        description={
          <>
            <div>
              <b>Preview de esta pantalla:</b> se actualiza al instante mientras
              modificas los campos, sin necesidad de guardar.
            </div>
            <div style={{ marginTop: 4 }}>
              <b>Resto del sistema</b> (las fotos que se suben desde Tareas,
              Inventario, Finanzas empresa y las Notas del aseo, y el editor de
              fotos de Tareas, Inventario y la foto de perfil): solo usan los
              valores nuevos <b>después de presionar "Guardar configuración"</b>.
              Hasta entonces siguen usando los anteriores. Las fotos que ya
              estaban subidas no cambian.
            </div>
            <div style={{ marginTop: 4 }}>
              Las demás pantallas con fotos (Flujos de caja, información para el
              huésped, Agenda de contactos y Costos sugeridos) no pasan por esta
              compresión: suben la foto tal como se elige.
            </div>
            <div style={{ marginTop: 4 }}>
              Sesiones ya abiertas en otros dispositivos pueden tardar hasta 5
              minutos en tomar el cambio (o refresca la página para forzarlo).
            </div>
            <div style={{ marginTop: 4, color: "#666" }}>
              {supportsWebP()
                ? "Tu navegador soporta WebP — el formato 'auto' generará WebP."
                : "Tu navegador no soporta WebP — el formato 'auto' generará JPEG."}
            </div>
          </>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Lo que no se configura aquí"
        description={
          <>
            <div>
              <b>Fotos del iPhone (HEIC):</b> en las pantallas que comprimen, se
              convierten a JPEG en el navegador antes de aplicar estos
              parámetros.
            </div>
            <div style={{ marginTop: 4 }}>
              <b>Miniaturas:</b> de cada foto el servidor genera una miniatura
              automática de 400 px (no configurable) que las listas y grillas
              usan para gastar menos datos.
            </div>
            <div style={{ marginTop: 4 }}>
              <b>Tope de peso:</b> el servidor rechaza fotos de más de 50 MB.
            </div>
          </>
        }
        data-testid="img-opt-fijos"
      />

      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={IMAGE_OPT_DEFAULTS}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="maxSide"
              label={renderLabel("Tamaño máximo (px)", HELP.maxSide)}
              rules={[
                { required: true, message: "Requerido" },
                {
                  type: "number",
                  min: 320,
                  max: 8000,
                  message: "Entre 320 y 8000",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={320} max={8000} step={100} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="minSide"
              label={renderLabel("Tamaño mínimo (px)", HELP.minSide)}
              rules={[
                { required: true, message: "Requerido" },
                {
                  type: "number",
                  min: 128,
                  max: 4000,
                  message: "Entre 128 y 4000",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={128} max={4000} step={50} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="quality"
              label={renderLabel("Calidad (0.1–1.0)", HELP.quality)}
              rules={[
                { required: true, message: "Requerido" },
                {
                  type: "number",
                  min: 0.1,
                  max: 1,
                  message: "Entre 0.1 y 1.0",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0.1}
                max={1}
                step={0.05}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="targetKB"
              label={renderLabel("Peso objetivo (KB)", HELP.targetKB)}
              rules={[
                { required: true, message: "Requerido" },
                {
                  type: "number",
                  min: 20,
                  max: 20000,
                  message: "Entre 20 y 20000",
                },
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={20} max={20000} step={50} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="format"
              label={renderLabel("Formato de salida", HELP.format)}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Select options={FORMAT_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>

        <Space style={{ marginBottom: 24 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            Guardar configuración
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleRestoreDefaults}>
            Restaurar valores por defecto
          </Button>
          {savedSettings && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Guardado: maxSide {savedSettings.maxSide}px · quality{" "}
              {savedSettings.quality} · target {savedSettings.targetKB}KB ·{" "}
              {savedSettings.format}
            </Text>
          )}
        </Space>
      </Form>

      <Title level={5} style={{ marginTop: 24 }}>
        <PictureOutlined /> Probar configuración
      </Title>
      <Text type="secondary">
        Sube una foto para ver lado a lado cómo quedará con los parámetros
        actuales. El preview no guarda nada — solo muestra el resultado.
      </Text>

      {!originalImage && (
        <div style={{ marginTop: 12 }}>
          <Upload.Dragger
            accept="image/*"
            multiple={false}
            showUploadList={false}
            beforeUpload={handleFileUpload}
            style={{ padding: 12 }}
          >
            <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
              <InboxOutlined />
            </p>
            <p className="ant-upload-text" style={{ margin: 0 }}>
              Arrastra una imagen o haz clic para seleccionarla
            </p>
          </Upload.Dragger>
        </div>
      )}

      {originalImage && (
        <>
          <div
            style={{
              marginTop: 16,
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <Space wrap>
              <Button
                icon={<DeleteOutlined />}
                onClick={handleClearImage}
                danger
              >
                Eliminar imagen
              </Button>
              <Upload
                accept="image/*"
                multiple={false}
                showUploadList={false}
                beforeUpload={handleFileUpload}
              >
                <Button icon={<PictureOutlined />}>Cambiar por otra</Button>
              </Upload>
              <Tooltip
                title={
                  fullSize
                    ? "Viendo a resolución natural (como en la galería de Tareas y el lightbox). Cambia a Ajustado para ver una miniatura que cabe en la tarjeta."
                    : "Viendo miniatura ajustada a la tarjeta (~320 px). A este tamaño cualquier imagen se ve aceptable. Cambia a Tamaño real para ver cómo queda realmente en la galería de Tareas."
                }
              >
                <Button
                  icon={fullSize ? <CompressOutlined /> : <ExpandOutlined />}
                  onClick={() => setFullSize((v) => !v)}
                >
                  {fullSize ? "Ajustado" : "Tamaño real"}
                </Button>
              </Tooltip>
            </Space>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: "1 1 240px",
                minWidth: 220,
              }}
            >
              <Tooltip title="Zoom de la lupa. También puedes usar la rueda del mouse sobre cualquiera de las dos imágenes.">
                <ZoomInOutlined style={{ color: "#666" }} />
              </Tooltip>
              <Text style={{ whiteSpace: "nowrap" }}>Zoom lupa:</Text>
              <Slider
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={0.1}
                value={zoom}
                onChange={(v) => setZoom(clampZoom(v as number))}
                style={{ flex: 1, minWidth: 140 }}
              />
              <Text strong style={{ minWidth: 40, textAlign: "right" }}>
                ×{zoom.toFixed(1)}
              </Text>
            </div>
          </div>
          <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
            Pasa el mouse sobre cualquiera de las dos imágenes: la lupa aparece
            en la misma posición en ambas para comparar detalles. Rueda del
            mouse = zoom.
          </Text>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card
                title="Original"
                size="small"
                styles={{ body: { padding: 12 } }}
              >
                <div
                  style={{
                    background: "#f5f5f5",
                    display: "flex",
                    alignItems: fullSize ? "flex-start" : "center",
                    justifyContent: fullSize ? "flex-start" : "center",
                    minHeight: 240,
                    maxHeight: fullSize ? "70vh" : undefined,
                    borderRadius: 4,
                    overflow: fullSize ? "auto" : "hidden",
                  }}
                >
                  <MagnifierImage
                    src={originalImage.dataUrl}
                    alt="Original"
                    lensPos={lensPos}
                    onLensChange={setLensPos}
                    zoom={zoom}
                    onZoomDelta={handleZoomDelta}
                    fullSize={fullSize}
                  />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text strong>{formatBytes(originalImage.bytes)}</Text>
                  <Text type="secondary" style={{ marginLeft: 12 }}>
                    {originalImage.width} × {originalImage.height} px
                  </Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card
                title={
                  <Space>
                    Optimizada
                    {optimizing && <Spin size="small" />}
                  </Space>
                }
                size="small"
                styles={{ body: { padding: 12 } }}
              >
                <div
                  style={{
                    background: "#f5f5f5",
                    display: "flex",
                    alignItems: fullSize ? "flex-start" : "center",
                    justifyContent: fullSize ? "flex-start" : "center",
                    minHeight: 240,
                    maxHeight: fullSize ? "70vh" : undefined,
                    borderRadius: 4,
                    overflow: fullSize ? "auto" : "hidden",
                  }}
                >
                  {optimizedImage ? (
                    <MagnifierImage
                      src={optimizedImage.dataUrl}
                      alt="Optimizada"
                      lensPos={lensPos}
                      onLensChange={setLensPos}
                      zoom={zoom}
                      onZoomDelta={handleZoomDelta}
                      fullSize={fullSize}
                    />
                  ) : (
                    <Text type="secondary">
                      {optimizing ? "Optimizando..." : "Sin preview"}
                    </Text>
                  )}
                </div>
                {optimizedImage && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>{formatBytes(optimizedImage.bytes)}</Text>
                    <Text type="secondary" style={{ marginLeft: 12 }}>
                      {optimizedImage.width} × {optimizedImage.height} px
                    </Text>
                    {reductionPct !== null && (
                      <Text
                        type={reductionPct > 0 ? "success" : "warning"}
                        style={{ marginLeft: 12 }}
                      >
                        {reductionPct > 0
                          ? `−${reductionPct.toFixed(1)}% vs original`
                          : `+${Math.abs(reductionPct).toFixed(1)}% vs original`}
                      </Text>
                    )}
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
